<template>
  <Loading v-if="componentLoading" :style="style" />
  <Chart v-else-if="visible" class="compare-line-chart" :option="chartConfig" :style="style"></Chart>
</template>

<script lang="ts" setup>
import Chart from "@p-v/packages/Charts/BaseChart/index.vue"
import Loading from "@p-v/packages/Loading.vue"
import { ref } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import config from "./config"
import type { DataItem, CompareLineChartProps } from "./index.d"
import type { EChartsOption, LineSeriesOption, XAXisComponentOption, YAXisComponentOption } from "echarts"

import useToggle from "@p-v/hooks/useToggle"

const componentLoading = ref<boolean>(true)

const props = withDefaults(defineProps<CompareLineChartProps>(), {
  dataList: () => [],
  config: () => ({}),
  unitShow: () => [false, false],
  pointerTheme: () => "double",
  style: () => ({}),
  show: () => true,
})
const { visible, show, hide, changeVisible } = useToggle(props.show)

const chartConfig = ref<any>(cloneDeep(config))

const fillTpl = <T,>(data: T, index: number, seriesTpl: T | T[]): T => {
  if (seriesTpl instanceof Array) {
    if (seriesTpl[index]) {
      return defaultsDeep(data, seriesTpl[index])
    } else {
      return defaultsDeep(data, seriesTpl[0])
    }
  } else if (seriesTpl) {
    return defaultsDeep(data, seriesTpl)
  } else {
    return data
  }
}

const getTpl = <T,>(key: string, config: object) => {
  if (config && config[key]) {
    let tpl: T
    tpl = config[key]
    delete config[key]
    return tpl
  }
  return {} as T
}

/**
 * 数据处理
 * @param config
 * @param dataList
 */
function componentPropEngine(propsData: CompareLineChartProps): EChartsOption {
  const { data, unitShow, config: _config } = propsData
  let seriesTpl: LineSeriesOption = getTpl("series", config)
  if (props.config && props.config.series) {
    seriesTpl = getTpl("series", props.config.series)
  }
  const newConfig = cloneDeep(config)
  const configFromProp = cloneDeep(_config)
  const configResult = defaultsDeep(configFromProp, props.config, newConfig)
  const yAxisConfig = getTpl<YAXisComponentOption>("yAxis", configResult)
  const xAxisConfig = getTpl<XAXisComponentOption>("xAxis", configResult)

  let sourceData = [] as (DataItem | undefined)[]

  if (Array.isArray(data)) {
    sourceData = data
  } else {
    sourceData = [data]
  }

  configResult.xAxis = []
  configResult.yAxis = []
  configResult.legend.data = []
  configResult.series = []

  let hasData = false

  for (let i = 0; i < sourceData.length; i++) {
    const item = sourceData[i]
    if (!item) {
      continue
    }
    configResult.legend.data.push(item.name)
    if (item.data.length === 0) {
      continue
    }
    if (!hasData) {
      hasData = true
    }
    const serie: LineSeriesOption = fillTpl(
      {
        name: item.name,
        type: "line",
        smooth: true,
        data: item.data,
        emphasis: {
          focus: "series",
        },
        symbol: item.data.length === 1 ? "circle" : "none",
        ...item.config?.serie,
      },
      i,
      seriesTpl,
    )

    let showXAxis = item.showXAxis
    if (i === 0 && item.showXAxis === undefined) {
      showXAxis = true
    }

    if (item.axis?.length > 0 && configResult.xAxis?.length < 2) {
      const xAxis: XAXisComponentOption = fillTpl(
        {
          data: item.axis,
          show: showXAxis,
          ...item.config?.xAxis,
        } as XAXisComponentOption,
        i,
        xAxisConfig,
      )
      if (props.pointerTheme === "one" && i === 0) {
        configResult.xAxis.push(xAxis)
      } else if (props.pointerTheme !== "one") {
        configResult.xAxis.push(xAxis)
      }
      // serie.xAxisIndex = xIndex
    }

    if (configResult.xAxis.length === 1) {
      serie.xAxisIndex = 0
    } else {
      if (configResult.xAxis.length >= 2) {
        serie.xAxisIndex = i % 2 === 0 ? 0 : 1
      }
    }
    let yAxisOpt = (item.config?.yAxis || {}) as YAXisComponentOption
    if (unitShow && unitShow[i]) {
      yAxisOpt.name = item?.unit
    }

    let showYAxis = item.showYAxis
    if (i === 0 && item.showYAxis === undefined) {
      showYAxis = true
    }
    const yAxisItem: YAXisComponentOption = fillTpl(yAxisOpt, i, yAxisConfig)
    let yIndex: number | undefined
    if (yAxisItem && showYAxis && configResult.yAxis?.length < 2) {
      yIndex = configResult.yAxis.push(yAxisItem) - 1
    }
    if (yIndex !== undefined) {
      serie.yAxisIndex = yIndex
    }
    configResult.series.push(serie)
  }

  interface AxisPointerParams {
    value: string | number
    seriesData: { data: string | number; name: string; seriesName: string }[]
  }

  const doubleOpinterFormat = (params: AxisPointerParams) => {
    let tpl = `${params.value}`
    params.seriesData.forEach(item => {
      tpl += ` ${item.seriesName}：${item.data} ${sourceData[0]!.unit} `
    })
    return tpl
  }

  if (hasData) {
    // console.log("props.pointerTheme", props.pointerTheme)
    if (props.pointerTheme === "double") {
      if (configResult.xAxis.length === 2) {
        configResult.xAxis[0].axisPointer = defaultsDeep(
          {
            show: true,
            label: {
              formatter: doubleOpinterFormat,
            },
          },
          configResult.xAxis[0].axisPointer,
        )
        configResult.xAxis[1].axisPointer = defaultsDeep(
          {
            show: true,
            label: {
              formatter: doubleOpinterFormat,
            },
          },
          configResult.xAxis[1].axisPointer,
        )
      }
    } else if (props.pointerTheme === "one") {
      configResult.xAxis[0].axisPointer = defaultsDeep(
        {
          show: true,
          label: undefined,
        },
        configResult.xAxis[0].axisPointer,
      )
      configResult.tooltip = defaultsDeep(
        {
          trigger: "axis",
          axisPointer: {
            type: "line",
            axis: "auto",
          },
        },
        configResult.tooltip,
      )
    }
    configResult.graphic[0].invisible = true
  } else {
    configResult.graphic[0].invisible = false
    configResult.series = []
  }

  console.log("configResult", configResult)

  return configResult
}
const loading = () => {
  componentLoading.value = true
}
const setData = (
  data:
    | DataItem[]
    | {
        data: DataItem | DataItem[]
        config?: EChartsOption
        unitShow?: [boolean, boolean]
      },
  option?: EChartsOption,
  unitShow?: [boolean, boolean],
) => {
  componentLoading.value = false
  if (Array.isArray(data)) {
    const result = componentPropEngine({ data, config: option, unitShow })
    chartConfig.value = result as any
  } else {
    const result = componentPropEngine(data)
    chartConfig.value = result as any
  }
}
defineExpose({ setData, loading, show, hide, changeVisible })
</script>

<style lang="scss">
.compare-line-chart {
  height: 100%;
}
</style>
.
